import React from "react";
import { Popup, List, Icon, Button } from "semantic-ui-react";
import { messages } from "../../../../config/constants";
import { Role } from "../../../../types/roles";
import { User, UserClaims } from "../../../../types/users";
import { exists, doesNotExist, usersWithRole } from "../../../../utils/utils";
import ActionsMenu from "../../../common/ActionsMenu";
import DismissibleMessage from "../../../common/DismissibleMesage";
import SearchableTable from "../../../common/SearchableTable";
import CancelInvitationModal from "./CancelInvitationModal";
import DeleteUserModal from "./DeleteUserModal";
import EditUserModal from "./EditUserModal";
import InviteUserToOrganizationForm from "./InviteUserToOrganizationForm";
import ResendInvitationModal from "./ResendInvitationModal";

type UserRoleProps = {
  user: UserClaims | Record<string, never>;
  users: User[];
  canEdit: boolean;
  canInvite: boolean;
  onInvite: (invitee: {
    firstName: string;
    lastName: string;
    email: string;
    roleCode: string;
  }) => void;
  onEditUser: (id: number, roles: number[]) => void;
  onDeleteUser: (b: number) => void;
  onCancelInvite: (c: number) => void;
  onReinviteUser: (d: number) => void;
  roles: Role[];
  disabled: boolean;
  isExternal: boolean;
  formatDateAsShortDate: (dateString: string) => string;
  regionDB?: string;
};

type UserRoleState = {
  selectedUser: UserClaims | Record<string, never>;
  showDeleteUserModal: boolean;
  showEditUserModal: boolean;
  showInviteUserModal: boolean;
  showResendInvitationModal: boolean;
  showCancelInvitationModal: boolean;
};

class UsersRoles extends React.PureComponent<UserRoleProps, UserRoleState> {
  state: UserRoleState = {
    selectedUser: {},
    showDeleteUserModal: false,
    showEditUserModal: false,
    showInviteUserModal: false,
    showResendInvitationModal: false,
    showCancelInvitationModal: false
  };

  getUserCellValue = (user: UserClaims) => {
    const isNameInvalid = doesNotExist(user.name) || user.name === "None";
    return isNameInvalid ? user.email : user.name;
  };

  userCellRenderer = (props: any) => {
    const user = props.value;
    let actions;
    const accepted = user.accepted;
    const expired = !accepted && new Date(user.invitation.expiresAt) < new Date();
    const relevantDate = !accepted
      ? new Date(user.invitation.expiresAt)
      : user.invitation && new Date(user.invitation.acceptedAt);
    const formattedDateString =
      relevantDate &&
      ` ${relevantDate.getMonth() + 1}/${relevantDate.getDate()}/${relevantDate.getFullYear()}`;
    let statusString = accepted
      ? "Invitation accepted"
      : expired
      ? "Invitation expired"
      : "Invitation expires on";
    if (formattedDateString) {
      statusString = `${statusString} ${this.props.formatDateAsShortDate(formattedDateString)}`;
    }
    if (accepted) {
      actions = [
        <Button
          key="edit"
          basic
          onClick={() => {
            this.setState({ selectedUser: user });
            this.setState({ showEditUserModal: true });
          }}
        >
          Edit user role
        </Button>,
        <Button
          key="delete"
          basic
          onClick={() => {
            this.setState({ selectedUser: user });
            this.setState({ showDeleteUserModal: true });
          }}
        >
          Remove
        </Button>
      ];
    } else {
      actions = [
        <Button
          key="cancel"
          basic
          onClick={() => {
            this.setState({ selectedUser: user });
            this.setState({ showResendInvitationModal: true });
          }}
        >
          Resend invitation
        </Button>,
        <Button
          key="resend"
          basic
          onClick={() => {
            this.setState({ selectedUser: user });
            this.setState({ showCancelInvitationModal: true });
          }}
        >
          Cancel invitation
        </Button>
      ];
    }
    const { details } = user;
    const detailsComponentContentRender = (title: string, value: string) => (
      <>
        <b>{title}</b>
        <p>{value}</p>
      </>
    );

    const detailsComponent = (
      <Popup basic trigger={<Icon style={{ float: "right" }} color="grey" name="user" />}>
        {user.email && detailsComponentContentRender("Email:", user.email)}
        {details.jobTitle && detailsComponentContentRender("Job title:", details.jobTitle)}
        {details.phone && detailsComponentContentRender("Phone:", details.phone)}
        {details.organization &&
          detailsComponentContentRender("Organization:", details.organization)}
        {details.street && detailsComponentContentRender("Street:", details.street)}
        {details.city && detailsComponentContentRender("City:", details.city)}
        {details.state && detailsComponentContentRender("State / Province:", details.state)}
        {details.zip && detailsComponentContentRender("Zip / Postal Code:", details.zip)}
        {details.country && detailsComponentContentRender("Country:", details.country)}
      </Popup>
    );
    return (
      <>
        {accepted ? (
          <Popup basic content={statusString} trigger={<Icon name="circle" color="green" />} />
        ) : expired ? (
          <Popup basic content={statusString} trigger={<Icon name="circle" color="red" />} />
        ) : (
          <Popup basic content={statusString} trigger={<Icon name="circle" color="yellow" />} />
        )}
        {this.getUserCellValue(user)}
        {this.props.user.id !== user.id && (
          <>
            <ActionsMenu disabled={!this.props.canEdit || this.props.disabled} actions={actions} />
            {accepted && detailsComponent}
          </>
        )}
      </>
    );
  };

  roleCellRenderer = (props: any) => props.value.join(", ");

  columns = [
    {
      Header: "User",
      accessor: "user",
      filter: "user",
      Cell: this.userCellRenderer,
      sortType: (previous: any, current: any, id: number) => {
        const prev = (this.getUserCellValue(previous.values[id]) ?? "").toLowerCase();
        const curr = (this.getUserCellValue(current.values[id]) ?? "").toLowerCase();
        if (prev > curr) {
          return 1;
        } else if (prev < curr) {
          return -1;
        } else {
          return 0;
        }
      }
    },
    {
      Header: "Role",
      accessor: "role",
      filter: "array",
      Cell: this.roleCellRenderer,
      sortType: "basic"
    }
  ];

  filterTypesConditions = {
    user: (rowValue: UserClaims, filterValue: string) => {
      return String(this.getUserCellValue(rowValue))
        .toLowerCase()
        .includes(String(filterValue).toLowerCase());
    },
    array: (rowValues: string[], filterValue: string) =>
      rowValues.reduce(
        (recursion, value) =>
          recursion ||
          String(value)
            .toLowerCase()
            .includes(String(filterValue).toLowerCase()),
        false
      )
  };

  toggleInviteUserModal = () => {
    this.setState({ showInviteUserModal: !this.state.showInviteUserModal });
  };

  render() {
    const data = this.props.users.map(user => {
      const roles = exists(user.roles) ? user.roles : [user.invitation.role];
      return {
        user: user,
        role: (roles ?? [])
          .filter(role => role.regionId === this.props.user.selectedRegionId)
          .map(role => role.name)
      };
    });
    const admins = usersWithRole(this.props.users, "ADMIN");
    const amountOfViewers = usersWithRole(this.props.users, "VIEWER").length;
    const amountOfModelers = usersWithRole(this.props.users, "MODELER").length;
    return (
      <>
        {this.state.showEditUserModal && (
          <EditUserModal
            user={this.state.selectedUser}
            regionDB={this.props.regionDB}
            onClose={() => this.setState({ showEditUserModal: false })}
            onEditUser={(roles: number[]) =>
              this.props.onEditUser(this.state.selectedUser.id, roles)
            }
            roles={this.props.roles}
            selectedRegionId={this.props.user.selectedRegionId}
          />
        )}
        {this.state.showDeleteUserModal && (
          <DeleteUserModal
            key="remove"
            user={this.state.selectedUser}
            onClose={() => this.setState({ showDeleteUserModal: false })}
            onDeleteUser={() => this.props.onDeleteUser(this.state.selectedUser.id)}
          />
        )}
        {this.state.showCancelInvitationModal && (
          <CancelInvitationModal
            user={this.state.selectedUser}
            onClose={() => this.setState({ showCancelInvitationModal: false })}
            // Commenting out unused prop
            // onDeleteUser={() => this.props.onDeleteUser(this.state.selectedUser.id)}
            onCancelInvite={() => this.props.onCancelInvite(this.state.selectedUser.invitation.id)}
          />
        )}
        {this.state.showResendInvitationModal && (
          <ResendInvitationModal
            user={this.state.selectedUser}
            onClose={() => this.setState({ showResendInvitationModal: false })}
            onReinviteUser={() => this.props.onReinviteUser(this.state.selectedUser.invitation.id)}
          />
        )}
        {this.props.isExternal && (
          <DismissibleMessage
            className={messages.hasExternalSubscription.className}
            content={messages.hasExternalSubscription.content}
          />
        )}
        <div style={{ display: "flex" }}>
          <span>Account Administrators ({admins.length}):</span>
          <List style={{ margin: "auto", marginLeft: "2%" }}>
            {admins.map(admin => (
              <List.Item style={{ paddingTop: "2%" }} key={admin.id}>
                {admin.name}
              </List.Item>
            ))}
          </List>
        </div>
        <List>
          <List.Item>Modelers: {amountOfModelers}</List.Item>
          <List.Item>Viewers: {amountOfViewers}</List.Item>
        </List>
        <InviteUserToOrganizationForm
          regionDB={this.props.regionDB}
          canInviteToPenciler={exists(
            this.props.user.roles?.find(
              role => role.code === "PENCILER_ADMIN" || role.code === "URBANSIM_ADMIN"
            )
          )}
          canInviteToUrbancanvas={exists(
            this.props.user.roles?.find(
              role => role.code === "URBANCANVAS_ADMIN" || role.code === "URBANSIM_ADMIN"
            )
          )}
          existingUsers={this.props.users}
          onSubmit={this.props.onInvite}
          disabled={this.props.disabled}
        />
        {/** @ts-ignore: Not typed yet */}
        <SearchableTable
          columns={this.columns}
          data={data}
          filterTypesConditions={this.filterTypesConditions}
        />
      </>
    );
  }
}

export default UsersRoles;
